import React, { useMemo } from 'react';
import { Upload, X } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { SUPPORTED_VIDEO_PLATFORMS, VIDEO_PLATFORMS_HINT } from '../../lib/videoPlatforms';
import { formatDuration, formatFileSize, estimateProcessingTime } from '../../utils/audioDuration';

/**
 * Панель настроек транскрибации.
 * Отображает кнопку выбора файла (или информацию о выбранном файле),
 * чекбокс диаризации и кнопку запуска транскрибации.
 *
 * @param {File|null} file - выбранный аудиофайл
 * @param {number|null} audioDuration - длительность аудио в секундах
 * @param {boolean} addDiarization - включена ли диаризация
 * @param {number} numSpeakers - точное количество участников (null = не задано)
 * @param {number} minSpeakers - нижняя граница вилки (null = не задана)
 * @param {number} maxSpeakers - верхняя граница вилки (null = не задана)
 * @param {boolean} isProcessing - идёт ли обработка
 * @param {Function} onFileClick - обработчик клика по кнопке выбора файла
 * @param {Function} onFileClear - обработчик удаления файла
 * @param {Function} onDiarizationChange - обработчик изменения чекбокса диаризации
 * @param {Function} onNumSpeakersChange - обработчик изменения количества участников
 * @param {Function} onStart - обработчик запуска транскрибации
 */
const SettingsBar = ({
  file,
  audioDuration,
  addDiarization,
  numSpeakers,
  minSpeakers,
  maxSpeakers,
  onSpeakerRangeChange,
  isProcessing,
  url,
  onUrlChange,
  onFileClick,
  onFileClear,
  onDiarizationChange,
  onNumSpeakersChange,
  onStart,
}) => {
  const eta = useMemo(
    () => estimateProcessingTime(audioDuration, addDiarization),
    [audioDuration, addDiarization]
  );

  const speakerOptions = useMemo(() => {
    const options = [{ value: '', label: 'Авто' }];
    for (let i = 2; i <= 20; i++) {
      options.push({ value: i, label: String(i) });
    }
    return options;
  }, []);

  const rangeOptions = useMemo(() => {
    const options = [{ value: '', label: '—' }];
    for (let i = 2; i <= 20; i++) {
      options.push({ value: i, label: String(i) });
    }
    return options;
  }, []);

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-paper-panel rounded-chip bg-paper-bg-xl">
      {/* Левая колонка: файл + ссылка */}
      <div className="flex flex-col gap-2 flex-1 min-w-0">
        {file ? (
          <div className="flex items-center gap-2 pl-3 pr-2 py-1.5 bg-paper-bg rounded-panel text-sm text-paper-accent min-w-0">
            <span className="text-base leading-none">🎵</span>
            <span className="truncate font-medium">{file.name}</span>
            <span className="text-paper-ink-3 whitespace-nowrap shrink-0">
              {formatFileSize(file.size)}
              {audioDuration != null && (
                <> • {formatDuration(audioDuration)} • ~{eta} мин обработки</>
              )}
            </span>
            <button type="button" onClick={onFileClear} disabled={isProcessing}
              className="ml-1 text-paper-ink-3 hover:text-paper-accent disabled:opacity-40 shrink-0"
              aria-label="Удалить файл">
              <X size={14} />
            </button>
          </div>
        ) : (
          <button type="button" onClick={onFileClick} disabled={isProcessing || !!url}
            className={`flex flex-col items-center justify-center gap-1.5 px-8 py-4 border-2 border-dashed  rounded-xl bg-paper-card hover: hover:bg-paper-bg transition-colors disabled:opacity-40 cursor-pointer min-w-[200px] ${url ? 'opacity-50' : ''}`}>
            <Upload size={22} className="text-paper-ink-3" />
            <span className="text-sm text-paper-ink-2">
              <span className="font-semibold text-paper-accent">Выберите файл</span> или перетащите
            </span>
            <span className="text-xs text-paper-hint">MP3, WAV, OGG, M4A, AAC, MKV</span>
          </button>
        )}

        {!file && (
          <>
            <div className="text-center text-xs text-paper-hint">— или —</div>
            <div className="flex items-center gap-2 px-3 py-2 bg-paper-card rounded-control bg-paper-bg focus-within: focus-within:ring-2 focus-within:ring-violet-100">
              <span className="text-sm">🔗</span>
              <input type="url" value={url} onChange={(e) => onUrlChange(e.target.value)}
                disabled={isProcessing} placeholder={VIDEO_PLATFORMS_HINT}
                className="flex-1 min-w-0 text-sm text-paper-ink outline-none bg-transparent disabled:opacity-40" />
            </div>
            <div className="text-[11px] text-paper-hint">
              {SUPPORTED_VIDEO_PLATFORMS}
            </div>
          </>
        )}
      </div>

      {/* Разделитель */}
      <div className="w-px h-6 bg-paper-track shrink-0" />

      {/* Диаризация */}
      <div className="flex items-center gap-2 shrink-0">
        <Checkbox
          id="diarization-checkbox"
          checked={addDiarization}
          onChange={(e) => onDiarizationChange(e.target.checked)}
          disabled={isProcessing || !!url}
        />
        <label
          htmlFor="diarization-checkbox"
          className={`text-sm select-none cursor-pointer ${
            addDiarization ? 'text-paper-accent font-medium' : 'text-paper-ink-2'
          } ${isProcessing ? 'opacity-40 cursor-default' : ''}`}
        >
          По участникам
        </label>
        {url && (
          <span className="text-[10px] text-paper-hint ml-1">недоступно для ссылок</span>
        )}

        {addDiarization && (
          <>
            <select
              value={numSpeakers === null ? '' : numSpeakers}
              onChange={(e) => onNumSpeakersChange(e.target.value === '' ? null : parseInt(e.target.value))}
              disabled={isProcessing}
              className="ml-1 text-sm bg-paper-bg text-paper-accent rounded-chip px-2 py-1 focus:outline-none focus:ring-2 focus:ring-paper-accent disabled:opacity-40"
              aria-label="Количество участников"
            >
              {speakerOptions.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {/* Вилка нужна ровно тогда, когда точного числа нет: модель без
                границ ищет от одного участника до бесконечности. Точное число
                вилку перебивает, поэтому при нём поля прячем. */}
            {numSpeakers === null && (
              <span className="flex items-center gap-1 text-xs text-paper-ink-3">
                от
                <select
                  value={minSpeakers === null ? '' : minSpeakers}
                  onChange={(e) => onSpeakerRangeChange(
                    e.target.value === '' ? null : parseInt(e.target.value), maxSpeakers)}
                  disabled={isProcessing}
                  className="text-sm bg-paper-bg text-paper-accent rounded-chip px-1.5 py-1 focus:outline-none focus:ring-2 focus:ring-paper-accent disabled:opacity-40"
                  aria-label="Участников не меньше"
                >
                  {rangeOptions.map(({ value, label }) => (
                    <option key={`min-${value}`} value={value}>{label}</option>
                  ))}
                </select>
                до
                <select
                  value={maxSpeakers === null ? '' : maxSpeakers}
                  onChange={(e) => onSpeakerRangeChange(
                    minSpeakers, e.target.value === '' ? null : parseInt(e.target.value))}
                  disabled={isProcessing}
                  className="text-sm bg-paper-bg text-paper-accent rounded-chip px-1.5 py-1 focus:outline-none focus:ring-2 focus:ring-paper-accent disabled:opacity-40"
                  aria-label="Участников не больше"
                >
                  {rangeOptions.map(({ value, label }) => (
                    <option key={`max-${value}`} value={value}>{label}</option>
                  ))}
                </select>
              </span>
            )}
          </>
        )}
      </div>

      {/* Спейсер */}
      <div className="flex-1" />

      {/* Кнопка запуска */}
      <button
        type="button"
        onClick={onStart}
        disabled={(!file && !url) || isProcessing}
        className={`px-5 py-2 rounded-panel text-sm font-semibold transition-all shrink-0 ${
          (file || url) && !isProcessing
            ? 'bg-paper-accent text-white hover:bg-paper-accent-hover shadow-none cursor-pointer'
            : 'bg-paper-track text-paper-hint cursor-not-allowed'
        }`}
      >
        Транскрибировать
      </button>
    </div>
  );
};

export default SettingsBar;
